import filesize from 'filesize';
import { IMAGE_FORMATS } from '../config/variables';
import SnackbarStore from '../stores/observables/SnackbarStore';

//
// File services
//

const fileToBase64 = (file: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });

// Basename = name + extension
export function getBasename(value: File | string, strict = false) {
  const name = typeof value === 'string' ? value : value.name;

  // Remove special characters to avoid for S3 with encoding issues
  // https://docs.aws.amazon.com/AmazonS3/latest/userguide/object-keys.html
  const regex = strict
    ? /[[\]`\\"#|%'{}<>~^?+]/g
    : // eslint-disable-next-line no-control-regex
      /[^\x00-\x7F]/g;
  return name.replace(regex, '_');
}

export function getExtension(value: File | string) {
  const filename = typeof value === 'string' ? value : value.name;
  // eslint-disable-next-line no-bitwise
  return filename.slice(((filename.lastIndexOf('.') - 1) >>> 0) + 2);
}

export function getFileFromInput(e: React.ChangeEvent<HTMLInputElement>) {
  e.persist();
  if (!(e.target.files && e.target.files.length)) {
    return null;
  }
  return e.target.files[0] || null;
}

// Name = file name without extension
export function getFilename(file: File | string) {
  const filename = getBasename(file, true);
  return filename.slice(0, filename.lastIndexOf('.'));
}

export function getFullFilename(file: File | string) {
  const filename = typeof file === 'string' ? file : file.name;
  return filename.slice(0, filename.lastIndexOf('.'));
}

interface GetImageOptions {
  onFailure?: (reason: string) => void;
  onSuccess?: (imageBase64: string) => void;
  supportedFormats?: string[];
}

export function getFileImage(file: File, options: GetImageOptions = {}) {
  const defaultFailure = (reason: string) => SnackbarStore.show(reason);
  const defaultSuccess = () => {};
  const onFailure = options.onFailure || defaultFailure;
  const onSuccess = options.onSuccess || defaultSuccess;
  const supportedFormats = options.supportedFormats ?? IMAGE_FORMATS;

  if (!supportedFormats.includes(file.type)) {
    return onFailure(
      `Unsupported file format, use: ${supportedFormats.join(', ')}`
    );
  }

  fileToBase64(file)
    .then((imageBase64: string) => onSuccess(imageBase64))
    .catch(() => onFailure('Error reading file'));

  return undefined;
}

export function formatSize(bytes: number) {
  const { value, symbol, exponent } = filesize(bytes, {
    round: 1,
    output: 'object',
  }) as unknown as {
    value: number;
    symbol: string;
    exponent: number;
  };
  const size = exponent <= 1 ? Math.round(value) : value;
  return `${size} ${symbol}`;
}
